import * as React from "react";
import { Box, Stack, TextField, MenuItem, Button, Typography, CircularProgress, Table, TableBody, TableHead, TableRow, TableCell } from "@mui/material";
import CalendarEditor from "./CalendarEditor";
import CheckIcon from "@mui/icons-material/Check";
import ErrorIcon from "@mui/icons-material/Error";

import { ApiContext } from "../../../App.js";

import { useTranslation } from "react-i18next";

import { v4 as uuidv4 } from 'uuid';

import HvacSelector from "./HvacSelector.js";

import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";


export default function ScheduleEditor(props) {

    const { facility } = props;

    const [hvacData, setHvacData] = React.useState([]);
    const [selectedHvacUrl, setSelectedHvacUrl] = React.useState(undefined);
    const [selectedSchedule, setSelectedSchedule] = React.useState([]);

    const [selectedBulkHvacUrls, setSelectedBulkHvacUrls] = React.useState([]);
    const [bulkSaveDialogOpen, setBulkSaveDialogOpen] = React.useState(false);
    const [bulkEditStatus, setBulkEditStatus] = React.useState([]);
    const [bulkProcessDialogOpen, setBulkProcessDialogOpen] = React.useState(false);


    const { hvacApi } = React.useContext(ApiContext);

    const { t } = useTranslation();

    const theme = useTheme();
    const fullScreen = useMediaQuery(theme.breakpoints.down("md"));


    React.useEffect(() => {

        hvacApi.list(
            facility,
            (data) => {

                const sortedData = data.toSorted(
                    (a, b) => (a.location + a.name) > (b.location + b.name) ? 1 : -1
                );

                setHvacData(sortedData);
                if (sortedData.length > 0) {
                    setSelectedHvacUrl(sortedData[0].url);
                    setSelectedSchedule(
                        data.find((item) => item.url === sortedData[0].url).schedule?.map((item) => ({ ...item, id: uuidv4() })) ?? []
                    );
                }
            },
            () => {
                window.alert(t("Couldn't retrieve HVAC data."))
                setHvacData([]);
            },
            true
        );
    }, [facility]);


    const handleChangeSelection = (url) => {
        setSelectedHvacUrl(url);
        setSelectedSchedule(hvacData.find((item) => item.url === url).schedule?.map((item) => ({ ...item, id: uuidv4() })) ?? []);
    }


    const handleSave = () => {

        hvacApi.update(
            selectedHvacUrl,
            {
                ...hvacData.find((item) => item.url === selectedHvacUrl),
                schedule: selectedSchedule.map((item) => {
                    const clone = { ...item };
                    delete clone.id;
                    return clone
                }
                ) // we do this to remove the ids we've added to list items
            },
            () => {
                window.alert(t("Schedule updated successfully."))
            },
            () => {
                window.alert(t("Couldn't update schedule."))
            },
            true
        );

    }

    const handleCalendarChange = (modified, extra) => {
        setSelectedSchedule(
            [...selectedSchedule.map((item) => item.id === modified.id ? modified : item), ...extra]
        )
    }

    const handleCalendarAdd = (item) => {
        setSelectedSchedule(
            [...selectedSchedule, item]
        )
    }

    const handleCalendarDelete = (id) => {
        setSelectedSchedule(
            selectedSchedule.filter((item) => item.id !== id)
        )
    }



    const handleBulkCloseWithoutSaving = () => {
        setBulkSaveDialogOpen(false);
    }

    const handleBulkProcessWithoutSaving = () => {
        setBulkProcessDialogOpen(false);
    }

    const handleBulkSave = () => {

        setBulkEditStatus(
            hvacData.filter((item) => selectedBulkHvacUrls.includes(item.url)).map((item) => ({
                url: item.url,
                location: item.location,
                name: item.name,
                status: "PENDING"
            }))
        )

        setBulkProcessDialogOpen(true);
        setBulkSaveDialogOpen(false);

        const onSuccess = (data) => {
            console.log("success")
            console.log(data.url)

            setBulkEditStatus(
                (old) => old.map((item) => item.url !== data.url ? item : { ...item, status: "OK" })
            )

            setHvacData(
                (old) => old.map((item) => item.url !== data.url ? item : data)
            )

        }

        const onError = (url, response) => {

            setBulkEditStatus(
                (old) => old.map((item) => item.url !== url ? item : { ...item, status: "ERROR", message: response })
            )

        }


        hvacData
            .filter((item) => selectedBulkHvacUrls.includes(item.url))
            .forEach((row) => {
                const payload = {
                    ...row,
                    schedule: selectedSchedule,
                };

                hvacApi.update(row.url, payload, onSuccess, (response) => onError(row.url, response));
            });

    }


    return (
        <Stack spacing={2} margin={3}>
            <Stack direction={"row"} spacing={2}>
                <TextField
                    id="hvac"
                    label={t("HVAC")}
                    onChange={(event) => { handleChangeSelection(event.target.value) }}
                    value={selectedHvacUrl ?? ""}
                    select
                >
                    {hvacData.map((option) => (
                        <MenuItem
                            key={option.url}
                            value={option.url}
                        >
                            {option.location} | {option.name}
                        </MenuItem>
                    ))}
                </TextField>
                <Button variant="contained" onClick={handleSave}>
                    {t("Apply")}
                </Button>
                <Button variant="outlined" onClick={() => setBulkSaveDialogOpen(true)}>
                    {t("Bulk apply")}
                </Button>
            </Stack>
            <Box
                height="calc(100vh - 230px)"
                maxHeight={1220}
                minWidth={700}
            >
                <CalendarEditor
                    schedule={selectedSchedule}
                    onChange={handleCalendarChange}
                    onAdd={handleCalendarAdd}
                    onDelete={handleCalendarDelete}
                />
            </Box>

            <Dialog
                open={bulkSaveDialogOpen}
                onClose={handleBulkCloseWithoutSaving}
                fullScreen={fullScreen}
                fullWidth
                maxWidth={"md"}
            >
                <DialogTitle>{t("Apply schedule in bulk")}</DialogTitle>
                <DialogContent>
                    <Box height={fullScreen ? "100%" : "calc(100vh - 500px)"}>
                        <HvacSelector
                            hvacPoints={hvacData}
                            selectedHvacPoints={selectedBulkHvacUrls}
                            setSelectedHvacPoints={setSelectedBulkHvacUrls}
                        />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={handleBulkCloseWithoutSaving}
                        autoFocus
                    >
                        {t("Cancel")}
                    </Button>
                    <Button onClick={handleBulkSave} disabled={selectedBulkHvacUrls.length === 0}>{t("Save")}</Button>
                </DialogActions>

            </Dialog>
            <Dialog
                open={bulkProcessDialogOpen}
                onClose={handleBulkProcessWithoutSaving}
                fullScreen={fullScreen}
                fullWidth
                maxWidth={"md"}
            >
                <DialogTitle>{t("Bulk apply status")}</DialogTitle>
                <DialogContent>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>{t("Location")}</TableCell>
                                <TableCell>{t("Name")}</TableCell>
                                <TableCell>{t("Status")}</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {
                                bulkEditStatus.map((item) =>
                                    <TableRow key={item.url}>
                                        <TableCell>
                                            {item.location}
                                        </TableCell>
                                        <TableCell>
                                            {item.name}
                                        </TableCell>
                                        <TableCell>
                                            {
                                                item.status === "PENDING" ?
                                                    <CircularProgress />
                                                    : (item.status === "OK" ? <CheckIcon /> : <ErrorIcon />)
                                            }
                                        </TableCell>
                                    </TableRow>
                                )
                            }
                        </TableBody>
                    </Table>
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={handleBulkProcessWithoutSaving}
                        autoFocus
                    >
                        {t("Done")}
                    </Button>
                </DialogActions>
            </Dialog>
        </Stack>

    )


};
